import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {ActivityIndicator, Animated, ScrollView, TouchableOpacity, View} from 'react-native';
import EStyleSheet from "react-native-extended-stylesheet";
import {CancelToken} from "axios";
import {connect} from 'react-redux';

import RolesService from "../../services/RolesService";
import ContentSection from "../../components/ContentSection";
import PageContentContainer from '../../components/PageContentContainer';
import {basicStyles} from "../../styles/basic";
import AppText from "../../components/AppText";
import {textStyles} from "../../styles/text";
import AppButton from "../../components/AppButton";
import {checkIfFluidAdmin, formatDate, generateUuid, objectFlip} from "../../utilities/methods";
import {setWarning} from "../../store/warning/warningActions";
import {bindActionCreators} from "redux";
import UploadOverlayHidden from "../../components/UploadOverlayHidden";
import AppTextInput from "../../components/AppTextInput";
import Modal from "../../components/Modal";
import {Col, Grid, Row} from "react-native-easy-grid";
import OrganizationsService from "../../services/OrganizationsService";
import UsersService from "../../services/UsersService";
import {inputStyles} from "../../styles/input";
import ToggleField from "../../components/ToggleField";
import {DEFAULT_ROLE_TOKENS} from "../../config";


function Roles(props) {
	const [roles, setRoles] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [organizationUsers, setOrganizationUsers] = useState([]);
	const [permissions, setPermissions] = useState([]);
	const [assignRole, setAssignRole] = useState(null);
	const [usersToAssign, setUsersToAssign] = useState([]);

	const {setWarning} = props;

	const updateDataRequestSources = useRef([]);

	const updateRole = useCallback((index, prop, value) => {
		setRoles(current => {
			let roles = [...current];
			roles[index][prop] = value;

			return roles;
		});
	}, [roles, setRoles])


	const organizationId = props.route?.params?.organizationId || props.auth.organization.organization_id;

	const saveRole = useCallback(async (roleIndex) => {
		let role;
		if (!roles[roleIndex].organization_role_id) {
			role = await RolesService.insertRole(organizationId, roles[roleIndex], organizationUsers.filter(user => user.organization_role_id === roles[roleIndex].organization_role_id || user.organization_role_id === roles[roleIndex].temp_key));
		} else {
			if (updateDataRequestSources.current[roleIndex]) {
				updateDataRequestSources.current[roleIndex].cancel('Request Canceled');
			}
			updateDataRequestSources.current[roleIndex] = CancelToken.source();
			role = await RolesService.updateRole(organizationId, roles[roleIndex].organization_role_id, roles[roleIndex], organizationUsers.filter(user => user.organization_role_id === roles[roleIndex].organization_role_id), updateDataRequestSources.current[roleIndex].token);
		}

		setRoles(current => {
			const roles = [...current];

			role.editing = false;

			roles.splice(roleIndex, 1, role)

			return roles;
		});

		fetchOrganizationUsers();
	}, [organizationId, roles]);



	const deleteRole = useCallback(async (roleIndex) => {
		if (roles[roleIndex].organization_role_id) {
			await RolesService.deleteRole(organizationId, roles[roleIndex].organization_role_id)
		}

		setRoles((current) => {
			const roles = [...current];

			roles.splice(roleIndex, 1);

			return roles;
		});

	}, [organizationId, roles]);

	const assignUsersToRole = useCallback(async (role) => {
		setOrganizationUsers(organizationUsers.map((organizationUser) => {
			if (usersToAssign.includes(organizationUser.user_id)) {
				organizationUser.organization_role_id = role.temp_key;
				return organizationUser;
			}
			return organizationUser;
		}))
	}, [organizationId, organizationUsers, roles, usersToAssign]);

	const handleCloseModal = useCallback(() => {
		setAssignRole(null);
	});

	const handleSaveModal = useCallback((role) => {
		assignUsersToRole(role);
		handleCloseModal();
	});

	const fetchOrganizationUsers = useCallback(() => {
		UsersService.getActiveUsers(organizationId, null, 'user_display_name')
			.then(users => {
				setOrganizationUsers(users);
			});
	});


	useEffect(() => {
		RolesService.getOrganizationRoles(organizationId)
			.then(roles => {
				setRoles(current => [...current, ...roles]);
				setIsLoading(false);
			});

		fetchOrganizationUsers();

		RolesService.getRolePermissions(organizationId)
			.then(permissions => {
				setPermissions(permissions);
			});
	}, [organizationId]);


	return (
		<>
			<ScrollView contentContainerStyle={basicStyles.flexScale} key={'roles-view'}>
				<PageContentContainer>
					<View
						style={[basicStyles.flexRow, basicStyles.justifyContentSpaceBetween, basicStyles.alignContentCenter, {marginBottom: 25}]}>
						<AppText style={[textStyles.pageSubTitle, textStyles.semiBold, {marginBottom: 0}]}>
							Roles
						</AppText>
						{checkIfFluidAdmin() && (
							<AppButton
								label="Add Role"
								action={() => {
									setRoles(current => {
										return [
											{temp_key: generateUuid(), editing: true, permissions: []},
											...current,
										];
									})
								}}
							/>
						)}
					</View>
					{
						roles.length > 0 ?
							roles.map((role, index) => {
								if (role.editing === true) {
									let roleUsers = organizationUsers.filter(user => user.organization_role_id === role.organization_role_id || user.organization_role_id === role.temp_key);
									return (
										<ContentSection
											key={role.organization_role_id || role.temp_key}
											style={[
											basicStyles.inputContainerPaddingVertical,
											basicStyles.inputContainerPaddingHorizontal,
											styles.roleContainer
										]}>

											<View style={[
												basicStyles.flexRow,
												basicStyles.alignContentCenter,
												basicStyles.justifyContentCenter,
												basicStyles.justifyContentSpaceBetween,
											]}>
												<View style={[basicStyles.inputWrapper, styles.inputWrapper, {flex: 1}]}>
													<AppTextInput
														label="Role Name"
														value={role.organization_role_title}
														onChangeText={value => {
															updateRole(index, 'organization_role_title', value);
														}}
													/>
												</View>
												<View style={[basicStyles.inputWrapper, styles.inputWrapper, {flex: 'none'}]}>
													<ToggleField
														label="Display Role on Team Section"
														value={parseInt(role.organization_role_hidden) === 1}
														onChange={value => {
															updateRole(index, 'organization_role_hidden', value ? '1' : '0');
														}}
													/>
												</View>
												{!DEFAULT_ROLE_TOKENS.includes(role.organization_role_token) && (
													<View style={[basicStyles.flexRow, basicStyles.justifyContentFlexEnd, {flex: 1}]}>
														<AppButton
															theme={'transRed'}
															label={'Delete'}
															action={() => {
																setWarning({
																	confirmAction: () => deleteRole(index),
																	confirmLabel: 'Confirm',
																	rejectLabel: 'Cancel',
																	warningMessage: <div><p>Are you sure you want to delete this role?</p><p>{role.organization_role_title}</p></div>,
																	active: true,
																});
															}}
														/>
													</View>
												)}
											</View>
											<View style={[
												basicStyles.flexRow,
												basicStyles.alignContentCenter,
												basicStyles.justifyContentCenter,
											]}>
												<View style={[basicStyles.inputWrapper, styles.inputWrapper]}>
													<AppText style={[textStyles.pageSubTitle, textStyles.semiBold, {marginBottom: 0, marginTop: 10}]}>Active Users</AppText>
												</View>
											</View>
											<View style={[
												basicStyles.flexRow,
												basicStyles.justifyContentFlexStart,
												styles.organizationUserContainer
											]}>
												{roleUsers.length > 0 ?
													roleUsers.map((user) => {
														return (
															<View
																key={role.organization_role_id + '-user-' + user.user_id}
																style={[
																basicStyles.flexRow,
																styles.organizationUserItem
																]}
															>
																<View style={[
																	basicStyles.flexRow,
																	inputStyles.inputWrapper,
																	styles.inputWrapper,
																	styles.organizationUserItemWrapper
																]}>
																	<AppText>
																		{user.user_display_name}
																	</AppText>
																	<AppButton
																		style={[styles.roleRowItem]}
																		label="Remove"
																		theme="transRed"
																		action={() => {
																			setOrganizationUsers(organizationUsers.map((organizationUser) => {
																				if (organizationUser.user_id === user.user_id) {
																					organizationUser.organization_role_id = null;
																					return organizationUser;
																				}
																				return organizationUser;
																			}))
																		}}
																	/>
																</View>
															</View>
														)
													})
												:
													(
														<View
															style={[
															basicStyles.flexRow,
															styles.organizationUserItem
														]}
														>
															<AppText>
																No users assigned to this role
															</AppText>
														</View>
												)}
											</View>
											{/*{role.organization_role_id && (*/}
												<View style={[
													basicStyles.flexRow,
													basicStyles.alignContentCenter,
												]}>
													<View style={[basicStyles.inputWrapper]}>
														<AppButton
															label={"Assign Users"}
															action={() => {
																setAssignRole(role);
															}}
														/>
													</View>
												</View>
											{/*)}*/}

											<View style={[
												basicStyles.flexRow,
												basicStyles.alignContentCenter,
												basicStyles.justifyContentCenter,
											]}>
												<View style={[basicStyles.inputWrapper, styles.inputWrapper]}>
													<AppText style={[textStyles.pageSubTitle, textStyles.semiBold, {marginBottom: 0, marginTop: 10}]}>Permissions</AppText>
												</View>
											</View>
											<View style={[
												basicStyles.flexRow,
												basicStyles.justifyContentSpaceBetween,
												styles.permissionContainer
											]}>
												{
													permissions
														.map((permission) => {
															return (
																<View
																	key={role.organization_role_id + '-perm-' + permission.permission_id}
																	style={[
																	basicStyles.flexRow,
																	styles.permissionItem,
																]}>
																	<ToggleField
																		label={permission.permission_title}
																		value={role.permissions.some((perm) => perm.permission_id === permission.permission_id)}
																		onChange={value => {
																			updateRole(index, 'permissions', value ? [...role.permissions, permission] : role.permissions.filter((perm) => perm.permission_id !== permission.permission_id))
																		}}
																	/>
																</View>
															)
														})
												}
											</View>

											<View style={[
												basicStyles.flexRow,
												basicStyles.alignContentCenter,
												basicStyles.justifyContentSpaceBetween,
												basicStyles.flexWrap
											]}>
												<AppButton
													label="Close"
													theme="transBlue"
													action={() => {
														updateRole(index, 'editing', false);
													}}
												/>

												<AppButton
													label="Save"
													action={() => {
														saveRole(index);
													}}
												/>
											</View>
										</ContentSection>
									);
								} else {

									return (
										<ContentSection
											key={role.organization_role_id || role.temp_key}
											style={[
												basicStyles.inputContainerPaddingVertical,
												styles.roleContainer
											]}>
											<View style={[
												basicStyles.flexRow,
												basicStyles.justifyContentSpaceBetween,
												styles.roleRow
											]}>
												<View style={[basicStyles.alignContentCenter, styles.roleRowFirstItem]}>
													<AppText>{role.organization_role_title}</AppText>
												</View>
												<View style={[styles.roleRowItem]}>
													<AppText>{organizationUsers.filter(user => user.organization_role_id === role.organization_role_id || user.organization_role_id === role.temp_key).length} Users</AppText>
												</View>
												<AppButton
													style={[styles.roleRowItem]}
													label="Edit"
													action={() => {
														updateRole(index, 'editing', true);
													}}
												/>
											</View>
										</ContentSection>
									);
								}
							})
							:
							isLoading ?
								<View style={[basicStyles.flexRow, basicStyles.flexCenterContent]} key={'loader'}>
									<AppText style={{marginLeft: 10, marginRight: 6}}>
										<ActivityIndicator size="large" color="#467AFF"/>
									</AppText>
									<AppText>Gathering Data</AppText>
								</View>
								:
								(
									<ContentSection style={[
										basicStyles.inputContainerPaddingVertical,
										basicStyles.inputContainerPaddingHorizontal,
										basicStyles.alignContentCenter,
										basicStyles.justifyContentCenter
									]}
					                key={'roles-message'}
									>
										<AppText>
											No Roles Currently Configured
										</AppText>
									</ContentSection>
								)
					}
				</PageContentContainer>
			</ScrollView>
			<Modal
				active={assignRole !== null}
				onClose={handleCloseModal}>
				<ContentSection
					style={[styles.contentWrapper]}
					onClick={e => {
						e.stopPropagation();
					}}
				>
					<AppText style={[textStyles.pageTitle, {marginBottom: 10}]}>
						Assign Users To {assignRole?.organization_role_title}
					</AppText>
					<ScrollView
						style={[styles.assignUserView]}
					>
						{
							assignRole !== null ?
								<View>
									{
										organizationUsers
											.filter(user => user.organization_role_id !== assignRole.organization_role_id)
											.map((user) => {
												return (
													<View
														key={'assign-user-' + user.user_id}
														style={[
														basicStyles.flexRow,
														inputStyles.inputWrapper,
														styles.inputWrapper,
														styles.organizationUserItemWrapper,
														styles.assignUserRow
													]}>
														<AppText style={[styles.assignUserItem, styles.assignUserFirstItem]}>
															{user.user_display_name}
														</AppText>
														<AppText style={[styles.assignUserItem, styles.assignUserFirstItem]}>
															{user.organization_role_title ?? 'No Role'}
														</AppText>
														<ToggleField
															value={usersToAssign.includes(user.user_id)}
															wrapperStyle={[styles.assignUserToggle]}
															onChange={value => {
																setUsersToAssign(currentValues => {
																	if (value) {
																		return [
																			...currentValues,
																			user.user_id
																		];
																	} else {
																		return currentValues.filter((currentValue) => currentValue !== user.user_id);
																	}
																});
															}}
														/>
													</View>
												);
											})
									}
								</View>
								: null
						}
					</ScrollView>
					<View
						style={[
							basicStyles.flexRow,
							styles.uploadContentButtons
						]}
					>
						<AppButton action={() => handleCloseModal()} theme="transBlue" label="Close"/>
						<AppButton action={() => handleSaveModal(assignRole)} theme="blue" label="Save"/>
					</View>
				</ContentSection>
			</Modal>
		</>
	);
}

const mapStateToProps = (state) => {
	const {auth} = state;
	return {auth};
};

const mapDispatchToProps = (dispatch) => (
	bindActionCreators({
		setWarning,
	}, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(Roles)

const styles = EStyleSheet.create({
	heading: {
		fontFamily: 'SourceSansPro-Bold',
		paddingLeft: 22,
		fontSize: 16,
		marginBottom: 10
	},
	subHeading: {
		fontFamily: 'SourceSansPro-Bold',
		paddingLeft: 22
	},
	toggleWrapper: {
		minWidth: 130
	},
	inputWrapper: {
		flex: 1
	},
	deleteIcon: {
		position: 'absolute',
		top: 15,
		right: 15
	},
	contentSection: {
		flexDirection: 'row',
		marginBottom: 50
	},
	roleContainer: {
		paddingLeft: 30,
		paddingRight: 30,
		justifyContent: 'space-between',
		marginBottom: 10
	},
	roleRowFirstItem: {
		width: '15%',
		flexDirection: 'row',
		justifyContent: 'flex-start'
	},
	uploadContentRow: {
		paddingLeft: 10,
		paddingRight: 10
	},
	roleContentItem: {
		fontSize: 15,
		paddingLeft: 10,
		paddingRight: 10,
		paddingTop: 5,
		paddingBottom: 5,
		flex: 1
	},
	'@media (max-width: 720)': {
		contentSection: {
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
		},
		contentItem: {
			width: '100%',
			borderBottomWidth: 1,
			borderBottomColor: '#E2E9F4'
		}
	},
	'@media (max-width: 750)': {
		headerContainer: {
			width: '100%',
		},
		roleContainer: {
			flexDirection: 'column',

		},
		roleRow: {
			flexDirection: 'column'
		},
		roleRowItem: {
			marginTop: 10,
		}
	},
	contentWrapper: {
		padding: 32,
		maxWidth: '90vw',
		maxHeight: '90vh'

	},
	columnHeading: {
		backgroundColor: '#f8fafe',
	},
	cell: {
		justifyContent: 'center',
		borderRightWidth: 1,
		borderRightColor: '#E2E9F4',
	},
	cellHead: {
		backgroundColor: '#F8FAFD',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-start',
		paddingRight: 30,
	},
	rowOdd: {
		backgroundColor: '#F8FAFD',
	},
	columnHeadText: {
		fontFamily: 'SourceSansPro-SemiBold',
		fontSize: 14,
		whiteSpace: 'nowrap',
	},
	outerGrid: {
		maxHeight: 400,
		overflowY: 'auto',
		borderColor: '#E2E9F4',
		borderBottomWidth: 1,
		borderTopWidth: 1,
		maxWidth: '100%',
	},
	grid: {
		borderLeftWidth: 1,
		borderRightWidth: 1,
		borderColor: '#E2E9F4',
		minWidth: '100%',
	},
	gridInversion: {
		transform:[{rotateX:'180deg'}]
	},
	columnFilterSectionPadding: {
		paddingLeft: 16,
		paddingRight: 16,
	},
	columnHeadLabel: {
		whiteSpace: 'nowrap',
	},
	row: {
		flexGrow: 1,
		flexShrink: 1,
		borderBottomWidth: 1,
		borderBottomColor: '#E2E9F4',
	},
	rowHead: {
		height: 40,
		flexBasis: 40,
	},
	rowBody: {
		height: 40,
		flexBasis: 40,
	},
	cellLast: {
		borderRightWidth: 0,
	},
	rowLast: {
		borderBottomWidth: 0,
	},
	spreadSheetContainer: {
		marginBottom: 40,
		zIndex: 1,
		elevation: 1,
		maxHeight: 600
	},
	cellBody: {
		justifyContent: 'center',
	},
	cellText: {
		paddingRight: 20,
		paddingLeft: 20,
		flexDirection: 'row',
		whiteSpace: 'nowrap',
		overflow: 'clip'
	},
	uploadContentButtons: {
		justifyContent: 'space-evenly',
		marginTop: 30
	},
	emailBodyInputWrapper: {
		padding: 0,
	},
	emailBodyLabelStyle: {
		paddingLeft: 15,
		paddingTop: 15,
		position: 'absolute',
		backgroundColor: 'white'
	},
	organizationUserContainer: {
		flexWrap: 'wrap',
		// gap: 15,
		// paddingRight: 15,
		// paddingLeft: 15,
		marginBottom: 20
	},
	organizationUserItem: {
		flexBasis: '20%',
		flexGrow: 0,
		padding: 15
	},
	organizationUserItemWrapper: {
		alignItems: 'center',
		justifyContent: 'space-between'
	},
	permissionContainer: {
		flexWrap: 'wrap',
		gap: 15,
		paddingRight: 15,
		paddingLeft: 15,
		marginBottom: 20
	},
	permissionItem: {
		flexBasis: '30%',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	assignUserView: {
		padding: 15,
		minWidth: 750
	},
	assignUserRow: {
		marginTop: 10,
		marginBottom: 10,
		height: 'auto',
		maxHeight: 60
	},
	assignUserItem: {
		width: '25%',
	},
	assignUserFirstItem: {
		width: '25%',
	},
	assignUserToggle: {
		border: 'none',
		alignSelf: 'flex-end',
		padding: 0,
		height: 'auto',
		maxHeight: 60,
		margin: 0
	}
});
